import { resolveCombatCore } from "./combat-core";

export const HEART_REGENT_SCHEMA = "vaelor-heart-regent/v1" as const;
export const HEART_REGENT_PUBLIC_SCHEMA = "vaelor-heart-regent-public/v1" as const;

export const HEART_REGENT_ROLES = Object.freeze([
  "observer",
  "purifier",
  "ravitailler",
  "tenir",
  "combattre"
] as const);

export type HeartRegentRole = typeof HEART_REGENT_ROLES[number];
export type HeartRegentMovement = "manifestation" | "fracture" | "contention";
export type HeartRegentAffiliation = "coalition" | "small-company" | "independent" | "relay";
export type HeartRegentPhase = "awaiting_manifest" | "active" | "contained" | "repelled";
export type HeartRegentOutcome = "contained" | "repelled";

export interface HeartRegentVariant {
  readonly syntheticOnly: true;
  readonly resistance: 0.8 | 1 | 1.2;
  readonly coverageRequired: 0.5 | 0.7 | 0.9;
  readonly individualCap: 1 | 1.5;
  readonly activeAnchors: 3 | 6;
  readonly toleratedBreaks: 1 | 2;
  readonly contentionWindowSeconds: 60 | 120;
}

export interface HeartRegentParticipant {
  readonly participantId: string;
  readonly affiliation: HeartRegentAffiliation;
  readonly role: HeartRegentRole;
  readonly capacity: number;
}

export interface HeartRegentManifest {
  readonly synthetic: true;
  readonly immutable: true;
  readonly manifestId: string;
  readonly chronicleId: string;
  readonly convergenceId: string;
  readonly rulesVersion: string;
  readonly attempt: number;
  readonly lockedAtMs: number;
  readonly participants: readonly HeartRegentParticipant[];
  readonly anchors: readonly string[];
  readonly roleDemand: Readonly<Record<HeartRegentRole, number>>;
}

export type HeartRegentCommandType =
  | "lock_manifest"
  | "contribute"
  | "break_anchor"
  | "repair_anchor"
  | "supply_interrupt"
  | "settle"
  | "open_throne"
  | "disconnect"
  | "crash";

export interface HeartRegentCommand {
  readonly deliveryId?: string;
  readonly commandId: string;
  readonly ordinal: number;
  readonly atMs: number;
  readonly type: HeartRegentCommandType;
  readonly chronicleId: string;
  readonly manifestId: string;
  readonly attempt: number;
  readonly manifest?: HeartRegentManifest;
  readonly participantId?: string;
  readonly claimedRole?: HeartRegentRole;
  readonly units?: number;
  readonly anchorId?: string;
  readonly anchorIds?: readonly string[];
  readonly checkpoint?: string;
}

export interface HeartRegentFunctionSnapshot {
  readonly rawUnits: number;
  readonly acceptedUnits: number;
  readonly coverage: number;
  readonly requiredCoverage: number;
  readonly thresholdMet: boolean;
  readonly usefulContributionObserved: boolean;
  readonly capBindingCount: number;
}

export interface HeartRegentPublicSummary {
  readonly movement: HeartRegentMovement;
  readonly functions: Readonly<Record<HeartRegentRole, {
    readonly state: "missing" | "partial" | "met";
    readonly coverage: number;
    readonly thresholdMet: boolean;
    readonly usefulContributionObserved: boolean;
  }>>;
  readonly pressure: {
    readonly aggregate: number;
    readonly signal: "stable" | "rising" | "critical";
  };
  readonly anchors: {
    readonly activeCount: number;
    readonly requiredCount: number;
    readonly brokenCount: number;
    readonly toleratedBreaks: number;
    readonly state: "missing" | "strained" | "stable";
  };
  readonly blockingCause: string;
  readonly nextActions: readonly string[];
  readonly globalDecision: "requires_all_five_functions_and_contention";
}

export interface HeartRegentPublicProjection {
  readonly schema: typeof HEART_REGENT_PUBLIC_SCHEMA;
  readonly phase: HeartRegentPhase;
  readonly verdict: "pending" | HeartRegentOutcome;
  readonly attempt: number | null;
  readonly summary: HeartRegentPublicSummary;
  readonly receipts: {
    readonly contained: boolean;
    readonly repelled: boolean;
  };
}

interface HeartRegentReceipt {
  readonly type: HeartRegentOutcome;
  readonly receiptId: string;
  readonly manifestId: string;
  readonly attempt: number;
  readonly settledAtMs: number;
}

interface HeartRegentRejection {
  readonly commandId: string;
  readonly code: string;
}

interface HeartRegentMetrics {
  readonly logicalMutationCount: number;
  readonly deliveryCount: number;
  readonly duplicateDeliveryCount: number;
  readonly divergentDuplicateCount: number;
  readonly rejectedCommandCount: number;
  readonly wrongChronicleCount: number;
  readonly wrongManifestCount: number;
  readonly wrongAttemptCount: number;
  readonly oldReceiptOrAttemptCount: number;
  readonly disconnectCount: number;
  readonly crashCount: number;
  readonly supplyInterruptCount: number;
  readonly combatCoreCalls: number;
  readonly combatResolvedBeforeWindow: number;
  readonly combatResolvedInsideWindow: number;
  readonly combatProgress: number;
  readonly combatRequirement: number;
  readonly capBindingCount: number;
  readonly usefulContributionByRole: Readonly<Record<HeartRegentRole, number>>;
  readonly usefulContributionByAffiliation: Readonly<Record<HeartRegentAffiliation, number>>;
  readonly movementTrace: readonly HeartRegentMovement[];
  readonly outcomeHistory: readonly HeartRegentOutcome[];
  readonly currentReceiptCount: number;
  readonly currentDossierCount: number;
  readonly terminalDuplicateNoLogicalMutation: boolean;
  readonly supplyChainIntact: boolean;
  readonly windowOpened: boolean;
  readonly contentionWindowEndsAtMs: number | null;
}

export interface HeartRegentBenchMetrics extends HeartRegentMetrics {
  readonly functions: Readonly<Record<HeartRegentRole, HeartRegentFunctionSnapshot>>;
  readonly anchors: {
    readonly activeCount: number;
    readonly requiredCount: number;
    readonly brokenCount: number;
    readonly toleratedBreaks: number;
    readonly heldCount: number;
    readonly ready: boolean;
  };
  readonly pressure: number;
  readonly blockingCause: string;
  readonly nextActions: readonly string[];
}

export interface HeartRegentState {
  readonly schema: typeof HEART_REGENT_SCHEMA;
  readonly variant: HeartRegentVariant;
  readonly lastServerNow: number;
  readonly deliveries: readonly HeartRegentCommand[];
  readonly phase: HeartRegentPhase;
  readonly movement: HeartRegentMovement;
  readonly attempt: number | null;
  readonly manifest: HeartRegentManifest | null;
  readonly functions: Readonly<Record<HeartRegentRole, HeartRegentFunctionSnapshot>>;
  readonly anchors: {
    readonly activeCount: number;
    readonly requiredCount: number;
    readonly brokenCount: number;
    readonly toleratedBreaks: number;
    readonly heldCount: number;
    readonly ready: boolean;
  };
  readonly pressure: number;
  readonly windowOpenedAtMs: number | null;
  readonly contentionWindowEndsAtMs: number | null;
  readonly combatProgress: number;
  readonly combatRequirement: number;
  readonly movementTrace: readonly HeartRegentMovement[];
  readonly receipt: HeartRegentReceipt | null;
  readonly repelledDossier: HeartRegentReceipt | null;
  readonly outcomeHistory: readonly HeartRegentReceipt[];
  readonly settlementCause: string | null;
  readonly rejections: readonly HeartRegentRejection[];
  readonly metrics: HeartRegentMetrics;
}

interface FunctionWork {
  rawUnits: number;
  acceptedUnits: number;
  coverage: number;
  usefulContributionObserved: boolean;
  capBindingCount: number;
  usefulEvents: number;
  contributorAffiliations: HeartRegentAffiliation[];
}

interface WorkState {
  phase: HeartRegentPhase;
  movement: HeartRegentMovement;
  attempt: number | null;
  manifest: HeartRegentManifest | null;
  functions: Record<HeartRegentRole, FunctionWork>;
  participantAccepted: Record<string, number>;
  heldAnchors: string[];
  brokenAnchors: string[];
  supplyChainIntact: boolean;
  lastSupplyAtMs: number | null;
  pressure: number;
  windowOpenedAtMs: number | null;
  contentionWindowEndsAtMs: number | null;
  combatProgress: number;
  combatRequirement: number;
  movementTrace: HeartRegentMovement[];
  receipt: HeartRegentReceipt | null;
  repelledDossier: HeartRegentReceipt | null;
  outcomeHistory: HeartRegentReceipt[];
  settlementCause: string | null;
  rejections: HeartRegentRejection[];
  logicalMutationCount: number;
  duplicateDeliveryCount: number;
  divergentDuplicateCount: number;
  wrongChronicleCount: number;
  wrongManifestCount: number;
  wrongAttemptCount: number;
  oldReceiptOrAttemptCount: number;
  disconnectCount: number;
  crashCount: number;
  supplyInterruptCount: number;
  combatCoreCalls: number;
  combatResolvedBeforeWindow: number;
  combatResolvedInsideWindow: number;
  usefulContributionByRole: Record<HeartRegentRole, number>;
  usefulContributionByAffiliation: Record<HeartRegentAffiliation, number>;
  capBindingCount: number;
  terminalDuplicateNoLogicalMutation: boolean;
}

const AFFILIATIONS = Object.freeze([
  "coalition",
  "small-company",
  "independent",
  "relay"
] as const);

const COMMAND_TYPES = Object.freeze([
  "lock_manifest",
  "contribute",
  "break_anchor",
  "repair_anchor",
  "supply_interrupt",
  "settle",
  "open_throne",
  "disconnect",
  "crash"
] as const);

function isRecord(value: unknown): value is Record<string, unknown> {
  if (typeof value !== "object" || value === null) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function freezeDeep<T>(value: T): T {
  if (typeof value === "object" && value !== null && !Object.isFrozen(value)) {
    for (const child of Object.values(value as Record<string, unknown>)) {
      if (typeof child === "object" && child !== null) freezeDeep(child);
    }
    Object.freeze(value);
  }
  return value;
}

function stable(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value) ?? "null";
  if (Array.isArray(value)) return `[${value.map((item) => stable(item)).join(",")}]`;
  const record = value as Record<string, unknown>;
  return `{${Object.keys(record).sort().map((key) => `${JSON.stringify(key)}:${stable(record[key])}`).join(",")}}`;
}

function round6(value: number): number {
  return Number(value.toFixed(6));
}

function positiveFinite(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

function safeInteger(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value);
}

function role(value: unknown): HeartRegentRole {
  if (typeof value !== "string" || !(HEART_REGENT_ROLES as readonly string[]).includes(value)) {
    throw new Error("heart_regent_role_invalid");
  }
  return value as HeartRegentRole;
}

function affiliation(value: unknown): HeartRegentAffiliation {
  if (typeof value !== "string" || !(AFFILIATIONS as readonly string[]).includes(value)) {
    throw new Error("heart_regent_affiliation_invalid");
  }
  return value as HeartRegentAffiliation;
}

function identifier(value: unknown, code: string): string {
  if (typeof value !== "string" || !/^[A-Za-z0-9][A-Za-z0-9._:-]{0,95}$/.test(value)) {
    throw new Error(code);
  }
  return value;
}

function allowedNumber<T extends number>(value: unknown, values: readonly T[], code: string): T {
  if (typeof value !== "number" || !values.includes(value as T)) throw new Error(code);
  return value as T;
}

function normalizeVariant(value: unknown): HeartRegentVariant {
  if (!isRecord(value) || value.syntheticOnly !== true) throw new Error("heart_regent_variant_not_synthetic");
  return freezeDeep({
    syntheticOnly: true as const,
    resistance: allowedNumber(value.resistance, [0.8, 1, 1.2] as const, "heart_regent_resistance_invalid"),
    coverageRequired: allowedNumber(value.coverageRequired, [0.5, 0.7, 0.9] as const, "heart_regent_coverage_invalid"),
    individualCap: allowedNumber(value.individualCap, [1, 1.5] as const, "heart_regent_individual_cap_invalid"),
    activeAnchors: allowedNumber(value.activeAnchors, [3, 6] as const, "heart_regent_active_anchors_invalid"),
    toleratedBreaks: allowedNumber(value.toleratedBreaks, [1, 2] as const, "heart_regent_tolerated_breaks_invalid"),
    contentionWindowSeconds: allowedNumber(
      value.contentionWindowSeconds,
      [60, 120] as const,
      "heart_regent_contention_window_invalid"
    )
  });
}

function normalizeManifest(value: unknown): HeartRegentManifest {
  if (!isRecord(value) || value.synthetic !== true || value.immutable !== true) {
    throw new Error("heart_regent_manifest_not_immutable");
  }
  const participantsValue = value.participants;
  if (!Array.isArray(participantsValue) || participantsValue.length === 0) {
    throw new Error("heart_regent_manifest_participants_invalid");
  }
  const participantIds = new Set<string>();
  const participants = participantsValue.map((candidate) => {
    if (!isRecord(candidate)) throw new Error("heart_regent_participant_invalid");
    const participantId = identifier(candidate.participantId, "heart_regent_participant_id_invalid");
    if (participantIds.has(participantId)) throw new Error("heart_regent_participant_duplicate");
    participantIds.add(participantId);
    if (!safeInteger(candidate.capacity) || candidate.capacity <= 0 || candidate.capacity > 100) {
      throw new Error("heart_regent_participant_capacity_invalid");
    }
    return {
      participantId,
      affiliation: affiliation(candidate.affiliation),
      role: role(candidate.role),
      capacity: candidate.capacity
    };
  });
  if (!Array.isArray(value.anchors) || value.anchors.length < 6) {
    throw new Error("heart_regent_manifest_anchors_invalid");
  }
  const anchors = value.anchors.map((candidate) => identifier(candidate, "heart_regent_anchor_invalid"));
  if (new Set(anchors).size !== anchors.length) throw new Error("heart_regent_anchor_duplicate");
  if (!isRecord(value.roleDemand)) throw new Error("heart_regent_role_demand_invalid");
  const roleDemand = {} as Record<HeartRegentRole, number>;
  for (const currentRole of HEART_REGENT_ROLES) {
    const demand = value.roleDemand[currentRole];
    if (!safeInteger(demand) || demand <= 0 || demand > 1000) {
      throw new Error("heart_regent_role_demand_invalid");
    }
    roleDemand[currentRole] = demand;
  }
  return freezeDeep({
    synthetic: true as const,
    immutable: true as const,
    manifestId: identifier(value.manifestId, "heart_regent_manifest_id_invalid"),
    chronicleId: identifier(value.chronicleId, "heart_regent_chronicle_id_invalid"),
    convergenceId: identifier(value.convergenceId, "heart_regent_convergence_id_invalid"),
    rulesVersion: identifier(value.rulesVersion, "heart_regent_rules_version_invalid"),
    attempt: safeInteger(value.attempt) && value.attempt > 0 ? value.attempt : (() => { throw new Error("heart_regent_attempt_invalid"); })(),
    lockedAtMs: safeInteger(value.lockedAtMs) && value.lockedAtMs >= 0 ? value.lockedAtMs : (() => { throw new Error("heart_regent_locked_at_invalid"); })(),
    participants,
    anchors,
    roleDemand
  });
}

function normalizeCommand(value: HeartRegentCommand): HeartRegentCommand {
  if (!isRecord(value)) throw new Error("heart_regent_command_invalid");
  if (typeof value.type !== "string" || !(COMMAND_TYPES as readonly string[]).includes(value.type)) {
    throw new Error("heart_regent_command_type_invalid");
  }
  if (!safeInteger(value.ordinal) || value.ordinal <= 0) throw new Error("heart_regent_command_ordinal_invalid");
  if (!safeInteger(value.atMs) || value.atMs < 0) throw new Error("heart_regent_command_time_invalid");
  const commandId = identifier(value.commandId, "heart_regent_command_id_invalid");
  const deliveryId = identifier(value.deliveryId ?? `${commandId}:${value.ordinal}`, "heart_regent_delivery_id_invalid");
  const normalized: HeartRegentCommand = {
    deliveryId,
    commandId,
    ordinal: value.ordinal,
    atMs: value.atMs,
    type: value.type as HeartRegentCommandType,
    chronicleId: identifier(value.chronicleId, "heart_regent_command_chronicle_invalid"),
    manifestId: identifier(value.manifestId, "heart_regent_command_manifest_invalid"),
    attempt: safeInteger(value.attempt) && value.attempt > 0 ? value.attempt : (() => { throw new Error("heart_regent_command_attempt_invalid"); })()
  };
  if (value.manifest !== undefined) (normalized as { manifest?: HeartRegentManifest }).manifest = normalizeManifest(value.manifest);
  if (value.participantId !== undefined) {
    (normalized as { participantId?: string }).participantId = identifier(value.participantId, "heart_regent_command_participant_invalid");
  }
  if (value.claimedRole !== undefined) (normalized as { claimedRole?: HeartRegentRole }).claimedRole = role(value.claimedRole);
  if (value.units !== undefined) {
    if (!positiveFinite(value.units) || value.units > 100000) throw new Error("heart_regent_command_units_invalid");
    (normalized as { units?: number }).units = value.units;
  }
  if (value.anchorId !== undefined) (normalized as { anchorId?: string }).anchorId = identifier(value.anchorId, "heart_regent_command_anchor_invalid");
  if (value.anchorIds !== undefined) {
    if (!Array.isArray(value.anchorIds)) throw new Error("heart_regent_command_anchors_invalid");
    (normalized as { anchorIds?: readonly string[] }).anchorIds = value.anchorIds.map((candidate) =>
      identifier(candidate, "heart_regent_command_anchor_invalid")
    );
  }
  if (value.checkpoint !== undefined) (normalized as { checkpoint?: string }).checkpoint = identifier(value.checkpoint, "heart_regent_checkpoint_invalid");
  return freezeDeep(normalized);
}

function commandSignature(command: HeartRegentCommand): string {
  const { deliveryId: _deliveryId, ordinal: _ordinal, ...logical } = command;
  return stable(logical);
}

function compareCommands(left: HeartRegentCommand, right: HeartRegentCommand): number {
  return left.atMs - right.atMs || left.ordinal - right.ordinal || left.commandId.localeCompare(right.commandId) ||
    (left.deliveryId ?? "").localeCompare(right.deliveryId ?? "");
}

function emptyFunctionWork(): Record<HeartRegentRole, FunctionWork> {
  const create = (): FunctionWork => ({
    rawUnits: 0,
    acceptedUnits: 0,
    coverage: 0,
    usefulContributionObserved: false,
    capBindingCount: 0,
    usefulEvents: 0,
    contributorAffiliations: []
  });
  return {
    observer: create(),
    purifier: create(),
    ravitailler: create(),
    tenir: create(),
    combattre: create()
  };
}

function emptyRoleNumbers(): Record<HeartRegentRole, number> {
  return Object.fromEntries(HEART_REGENT_ROLES.map((currentRole) => [currentRole, 0])) as Record<HeartRegentRole, number>;
}

function emptyAffiliationNumbers(): Record<HeartRegentAffiliation, number> {
  return Object.fromEntries(AFFILIATIONS.map((currentAffiliation) => [currentAffiliation, 0])) as Record<HeartRegentAffiliation, number>;
}

function makeWork(): WorkState {
  return {
    phase: "awaiting_manifest",
    movement: "manifestation",
    attempt: null,
    manifest: null,
    functions: emptyFunctionWork(),
    participantAccepted: {},
    heldAnchors: [],
    brokenAnchors: [],
    supplyChainIntact: true,
    lastSupplyAtMs: null,
    pressure: 0,
    windowOpenedAtMs: null,
    contentionWindowEndsAtMs: null,
    combatProgress: 0,
    combatRequirement: 0,
    movementTrace: ["manifestation"],
    receipt: null,
    repelledDossier: null,
    outcomeHistory: [],
    settlementCause: null,
    rejections: [],
    logicalMutationCount: 0,
    duplicateDeliveryCount: 0,
    divergentDuplicateCount: 0,
    wrongChronicleCount: 0,
    wrongManifestCount: 0,
    wrongAttemptCount: 0,
    oldReceiptOrAttemptCount: 0,
    disconnectCount: 0,
    crashCount: 0,
    supplyInterruptCount: 0,
    combatCoreCalls: 0,
    combatResolvedBeforeWindow: 0,
    combatResolvedInsideWindow: 0,
    usefulContributionByRole: emptyRoleNumbers(),
    usefulContributionByAffiliation: emptyAffiliationNumbers(),
    capBindingCount: 0,
    terminalDuplicateNoLogicalMutation: true
  };
}

function resetForManifest(work: WorkState, manifest: HeartRegentManifest, variant: HeartRegentVariant): void {
  work.phase = "active";
  work.movement = "manifestation";
  work.attempt = manifest.attempt;
  work.manifest = manifest;
  work.functions = emptyFunctionWork();
  work.participantAccepted = {};
  work.heldAnchors = [];
  work.brokenAnchors = [];
  work.supplyChainIntact = true;
  work.lastSupplyAtMs = null;
  work.pressure = Math.round(100 + variant.resistance * 100);
  work.windowOpenedAtMs = null;
  work.contentionWindowEndsAtMs = null;
  work.combatProgress = 0;
  work.combatRequirement = manifest.roleDemand.combattre * variant.resistance;
  work.movementTrace = ["manifestation"];
  work.receipt = null;
  work.repelledDossier = null;
  work.settlementCause = null;
  work.terminalDuplicateNoLogicalMutation = true;
}

function reject(work: WorkState, command: HeartRegentCommand, code: string): void {
  work.rejections.push({ commandId: command.commandId, code });
  if (code === "wrong_chronicle") work.wrongChronicleCount += 1;
  if (code === "wrong_manifest") work.wrongManifestCount += 1;
  if (code === "wrong_attempt") work.wrongAttemptCount += 1;
  if (code === "already_resolved" || code === "old_receipt") work.oldReceiptOrAttemptCount += 1;
}

function participantFor(work: WorkState, participantId: string | undefined): HeartRegentParticipant | null {
  if (!work.manifest || participantId === undefined) return null;
  return work.manifest.participants.find((candidate) => candidate.participantId === participantId) ?? null;
}

function envelopeFailure(work: WorkState, command: HeartRegentCommand): string | null {
  if (!work.manifest) return "manifest_missing";
  if (command.chronicleId !== work.manifest.chronicleId) return "wrong_chronicle";
  if (command.manifestId !== work.manifest.manifestId) return "wrong_manifest";
  if (command.attempt !== work.manifest.attempt) return "wrong_attempt";
  return null;
}

function activeAnchorIds(work: WorkState, variant: HeartRegentVariant): readonly string[] {
  return work.manifest?.anchors.slice(0, variant.activeAnchors) ?? [];
}

function anchorSnapshot(work: WorkState, variant: HeartRegentVariant): {
  activeCount: number;
  requiredCount: number;
  brokenCount: number;
  toleratedBreaks: number;
  heldCount: number;
  ready: boolean;
} {
  const active = activeAnchorIds(work, variant);
  const held = active.filter((anchorId) => work.heldAnchors.includes(anchorId));
  const broken = active.filter((anchorId) => work.brokenAnchors.includes(anchorId));
  const activeCount = held.filter((anchorId) => !broken.includes(anchorId)).length;
  return {
    activeCount,
    requiredCount: active.length,
    brokenCount: broken.length,
    toleratedBreaks: variant.toleratedBreaks,
    heldCount: held.length,
    ready: held.length >= active.length && broken.length <= variant.toleratedBreaks
  };
}

function applyAnchorIds(work: WorkState, command: HeartRegentCommand, variant: HeartRegentVariant, accepted: number): number {
  const active = activeAnchorIds(work, variant);
  if (active.length === 0 || accepted <= 0) return 0;
  const requested = command.anchorIds?.filter((anchorId) => active.includes(anchorId)) ?? [];
  const candidateIds = requested.length > 0 ? requested : active.slice(0, Math.max(1, Math.ceil(accepted)));
  let added = 0;
  for (const anchorId of candidateIds) {
    if (!work.heldAnchors.includes(anchorId)) {
      work.heldAnchors.push(anchorId);
      added += 1;
    }
  }
  return added;
}

function functionThresholdMet(work: WorkState, variant: HeartRegentVariant): boolean {
  if (!work.manifest) return false;
  return HEART_REGENT_ROLES.every((currentRole) =>
    work.functions[currentRole].coverage >= variant.coverageRequired
  );
}

function openWindowIfReady(work: WorkState, atMs: number, variant: HeartRegentVariant): void {
  const observer = work.functions.observer.usefulContributionObserved;
  const purifier = work.functions.purifier.usefulContributionObserved;
  if (observer && purifier && work.windowOpenedAtMs === null) {
    work.windowOpenedAtMs = atMs;
    work.pressure = Math.max(0, work.pressure - 8);
  }
  if (work.windowOpenedAtMs !== null && work.contentionWindowEndsAtMs === null) {
    const vulnerabilityEndsAt = work.windowOpenedAtMs + variant.contentionWindowSeconds * 1000;
    if (atMs > vulnerabilityEndsAt) work.pressure += 6;
  }
}

function appendMovement(work: WorkState, movement: HeartRegentMovement): void {
  if (work.movement === movement) return;
  work.movement = movement;
  if (!work.movementTrace.includes(movement)) work.movementTrace.push(movement);
}

function refreshMovement(work: WorkState, variant: HeartRegentVariant, atMs: number): void {
  if (!work.manifest) return;
  if (functionThresholdMet(work, variant) && work.movement === "manifestation") {
    appendMovement(work, "fracture");
  }
  openWindowIfReady(work, atMs, variant);
  if (functionThresholdMet(work, variant) && work.windowOpenedAtMs !== null &&
    work.combatProgress >= work.combatRequirement && work.movement !== "contention" &&
    work.phase === "active") {
    appendMovement(work, "contention");
    work.contentionWindowEndsAtMs = atMs + variant.contentionWindowSeconds * 1000;
  }
}

function resolveSyntheticCombat(units: number): ReturnType<typeof resolveCombatCore> {
  return resolveCombatCore({
    attacker: {
      sideId: "attacker",
      label: "Synthetic contributors",
      formationId: "regent-bench-attacker",
      groups: [{
        groupId: "synthetic-contributors",
        ownerId: "synthetic",
        unitKey: "synthetic-contributor",
        label: "Synthetic contributor",
        quantity: Math.max(1, Math.ceil(units)),
        hpUnit: 10,
        attack: 40,
        damageType: "synthetic-impact",
        armorType: "synthetic-shell",
        role: "front",
        secPerHex: 160,
        synthetic: true
      }]
    },
    defender: {
      sideId: "defender",
      label: "Synthetic Regent manifestation",
      formationId: "regent-bench-defender",
      groups: [{
        groupId: "synthetic-regent",
        ownerId: "synthetic",
        unitKey: "synthetic-regent",
        label: "Synthetic manifestation",
        quantity: 1,
        hpUnit: 10,
        attack: 1,
        damageType: "synthetic-pressure",
        armorType: "synthetic-shell",
        role: "front",
        secPerHex: 160,
        synthetic: true
      }]
    },
    formations: {
      "regent-bench-attacker": {
        label: "Synthetic attacker",
        init: 0,
        roleAtk: { front: 1 },
        roleDef: { front: 1 },
        morale: 0,
        retreat: 1
      },
      "regent-bench-defender": {
        label: "Synthetic defender",
        init: 0,
        roleAtk: { front: 1 },
        roleDef: { front: 1 },
        morale: 0,
        retreat: 1
      }
    },
    counters: { "synthetic-impact": { "synthetic-shell": 1 } },
    terrain: { label: "Synthetic arena", atk: 1, def: 1, moraleAtk: 0, moraleDef: 0 },
    maxRounds: 3
  });
}

function blockingCause(work: WorkState, variant: HeartRegentVariant): string {
  if (!work.manifest) return "manifest_missing";
  if (work.phase === "contained") return "contained";
  if (work.phase === "repelled" && work.settlementCause) return work.settlementCause;
  const missing = HEART_REGENT_ROLES.filter((currentRole) =>
    work.functions[currentRole].coverage < variant.coverageRequired
  );
  if (missing.length > 0) return `missing_function:${missing.join(",")}`;
  if (work.windowOpenedAtMs === null) return "vulnerability_window_missing";
  const anchors = anchorSnapshot(work, variant);
  if (!anchors.ready) return "anchors_unstable";
  if (!work.supplyChainIntact) return "supply_chain_interrupted";
  if (work.combatProgress < work.combatRequirement) return "combat_progress_insufficient";
  if (work.contentionWindowEndsAtMs !== null && work.lastSupplyAtMs !== null && work.lastSupplyAtMs < 0) {
    return "contention_window_expired";
  }
  if (work.contentionWindowEndsAtMs === null) return "contention_not_open";
  return "settlement_pending";
}

function functionSnapshots(work: WorkState, variant: HeartRegentVariant): Record<HeartRegentRole, HeartRegentFunctionSnapshot> {
  return Object.fromEntries(HEART_REGENT_ROLES.map((currentRole) => {
    const current = work.functions[currentRole];
    return [currentRole, {
      rawUnits: round6(current.rawUnits),
      acceptedUnits: round6(current.acceptedUnits),
      coverage: round6(current.coverage),
      requiredCoverage: variant.coverageRequired,
      thresholdMet: current.coverage >= variant.coverageRequired,
      usefulContributionObserved: current.usefulContributionObserved,
      capBindingCount: current.capBindingCount
    }];
  })) as Record<HeartRegentRole, HeartRegentFunctionSnapshot>;
}

function applyContribution(work: WorkState, command: HeartRegentCommand, variant: HeartRegentVariant): void {
  const participant = participantFor(work, command.participantId);
  if (!participant) {
    reject(work, command, "participant_not_in_manifest");
    return;
  }
  if (command.claimedRole !== undefined && command.claimedRole !== participant.role) {
    reject(work, command, "manifest_role_is_authoritative");
    return;
  }
  if (!positiveFinite(command.units)) {
    reject(work, command, "contribution_units_invalid");
    return;
  }
  if (work.phase === "contained" || work.phase === "repelled") {
    reject(work, command, "already_resolved");
    return;
  }
  const currentRole = participant.role;
  const functionWork = work.functions[currentRole];
  const previous = work.participantAccepted[participant.participantId] ?? 0;
  const cap = participant.capacity * variant.individualCap;
  const accepted = Math.min(command.units, Math.max(0, cap - previous));
  functionWork.rawUnits += command.units;
  if (accepted < command.units) {
    functionWork.capBindingCount += 1;
    work.capBindingCount += 1;
  }
  if (accepted <= 0) {
    work.logicalMutationCount += 1;
    return;
  }
  work.participantAccepted[participant.participantId] = round6(previous + accepted);
  functionWork.acceptedUnits += accepted;
  functionWork.coverage = Math.min(1, functionWork.acceptedUnits / work.manifest!.roleDemand[currentRole]);
  let useful = false;
  if (currentRole === "observer") {
    useful = true;
    work.pressure = Math.max(0, work.pressure - accepted * 2);
  } else if (currentRole === "purifier") {
    useful = work.functions.observer.usefulContributionObserved;
    if (useful) work.pressure = Math.max(0, work.pressure - accepted * 3);
  } else if (currentRole === "ravitailler") {
    useful = work.supplyChainIntact;
    if (useful) {
      work.lastSupplyAtMs = command.atMs;
      work.pressure = Math.max(0, work.pressure - accepted);
    }
  } else if (currentRole === "tenir") {
    useful = applyAnchorIds(work, command, variant, accepted) > 0;
    if (useful) work.pressure = Math.max(0, work.pressure - accepted);
  } else if (currentRole === "combattre") {
    work.combatCoreCalls += 1;
    const combat = resolveSyntheticCombat(accepted);
    const windowEnd = work.windowOpenedAtMs === null
      ? null
      : work.windowOpenedAtMs + variant.contentionWindowSeconds * 1000;
    const insideWindow = windowEnd !== null && command.atMs <= windowEnd;
    if (insideWindow) work.combatResolvedInsideWindow += 1;
    else work.combatResolvedBeforeWindow += 1;
    useful = insideWindow && combat.winner === "attacker";
    if (useful) {
      work.combatProgress += accepted;
      work.pressure = Math.max(0, work.pressure - accepted * 2);
    }
  }
  if (useful) {
    functionWork.usefulContributionObserved = true;
    functionWork.usefulEvents += 1;
    functionWork.contributorAffiliations.push(participant.affiliation);
    work.usefulContributionByRole[currentRole] += 1;
    work.usefulContributionByAffiliation[participant.affiliation] += 1;
  }
  work.logicalMutationCount += 1;
}

function applyCommand(work: WorkState, command: HeartRegentCommand, variant: HeartRegentVariant): void {
  if (command.type === "lock_manifest") {
    if (!command.manifest) {
      reject(work, command, "manifest_payload_missing");
      return;
    }
    let manifest: HeartRegentManifest;
    try {
      manifest = normalizeManifest(command.manifest);
    } catch {
      reject(work, command, "manifest_invalid");
      return;
    }
    if (manifest.manifestId !== command.manifestId || manifest.chronicleId !== command.chronicleId ||
      manifest.attempt !== command.attempt || manifest.lockedAtMs > command.atMs) {
      reject(work, command, "manifest_envelope_invalid");
      return;
    }
    if (work.manifest === null) {
      if (manifest.attempt !== 1) {
        reject(work, command, "first_attempt_invalid");
        return;
      }
      resetForManifest(work, manifest, variant);
      work.logicalMutationCount += 1;
      return;
    }
    if (work.phase !== "repelled") {
      reject(work, command, "manifest_already_locked");
      return;
    }
    if (manifest.attempt !== work.manifest.attempt + 1 || manifest.manifestId === work.manifest.manifestId ||
      manifest.convergenceId === work.manifest.convergenceId) {
      reject(work, command, "new_attempt_not_fresh");
      return;
    }
    resetForManifest(work, manifest, variant);
    work.logicalMutationCount += 1;
    return;
  }

  const failure = envelopeFailure(work, command);
  if (failure !== null) {
    reject(work, command, failure);
    return;
  }
  if (command.type === "crash") {
    work.crashCount += 1;
    return;
  }
  if (command.type === "disconnect") {
    if (!participantFor(work, command.participantId)) {
      reject(work, command, "participant_not_in_manifest");
      return;
    }
    work.disconnectCount += 1;
    return;
  }
  if (work.phase === "contained" || work.phase === "repelled") {
    if (command.type === "settle") reject(work, command, "already_resolved");
    else reject(work, command, "attempt_resolved");
    return;
  }
  if (command.type === "contribute") applyContribution(work, command, variant);
  else if (command.type === "supply_interrupt") {
    if (!work.supplyChainIntact) {
      reject(work, command, "supply_chain_already_interrupted");
      return;
    }
    work.supplyChainIntact = false;
    work.supplyInterruptCount += 1;
    work.pressure += 24;
    work.logicalMutationCount += 1;
  } else if (command.type === "break_anchor") {
    const active = activeAnchorIds(work, variant);
    if (command.anchorId === undefined || !active.includes(command.anchorId)) {
      reject(work, command, "anchor_unknown");
      return;
    }
    if (work.brokenAnchors.includes(command.anchorId)) {
      reject(work, command, "anchor_already_broken");
      return;
    }
    work.brokenAnchors.push(command.anchorId);
    work.pressure += 25;
    work.logicalMutationCount += 1;
  } else if (command.type === "repair_anchor") {
    if (command.anchorId === undefined || !work.brokenAnchors.includes(command.anchorId)) {
      reject(work, command, "anchor_not_broken");
      return;
    }
    work.brokenAnchors = work.brokenAnchors.filter((anchorId) => anchorId !== command.anchorId);
    work.pressure = Math.max(0, work.pressure - 25);
    work.logicalMutationCount += 1;
  } else if (command.type === "open_throne") {
    reject(work, command, "throne_open_out_of_scope");
  } else if (command.type === "settle") {
    const cause = blockingCause(work, variant);
    const contentWindowActive = work.movement === "contention" &&
      work.contentionWindowEndsAtMs !== null && command.atMs <= work.contentionWindowEndsAtMs;
    const anchors = anchorSnapshot(work, variant);
    const canContain = contentWindowActive && work.supplyChainIntact && anchors.ready &&
      work.combatProgress >= work.combatRequirement;
    const outcome: HeartRegentOutcome = canContain ? "contained" : "repelled";
    const receipt: HeartRegentReceipt = {
      type: outcome,
      receiptId: `heart-regent:${work.manifest!.manifestId}:${work.manifest!.attempt}:${outcome}`,
      manifestId: work.manifest!.manifestId,
      attempt: work.manifest!.attempt,
      settledAtMs: command.atMs
    };
    work.phase = outcome;
    work.receipt = receipt;
    work.outcomeHistory.push(receipt);
    work.settlementCause = outcome === "contained" ? "contained" :
      (cause === "settlement_pending" ? "contention_window_expired" : cause);
    if (outcome === "repelled") work.repelledDossier = receipt;
    work.logicalMutationCount += 1;
  }
}

function deriveState(
  variant: HeartRegentVariant,
  deliveries: readonly HeartRegentCommand[],
  lastServerNow: number
): HeartRegentState {
  const work = makeWork();
  const groups = new Map<string, HeartRegentCommand[]>();
  for (const delivery of deliveries) {
    const list = groups.get(delivery.commandId) ?? [];
    list.push(delivery);
    groups.set(delivery.commandId, list);
  }
  const representatives: HeartRegentCommand[] = [];
  for (const [commandId, group] of groups.entries()) {
    const signatures = new Set(group.map((command) => commandSignature(command)));
    if (signatures.size > 1) {
      work.divergentDuplicateCount += 1;
      work.rejections.push({ commandId, code: "command_id_conflict" });
      continue;
    }
    const representative = [...group].sort(compareCommands)[0];
    representatives.push(representative);
    work.duplicateDeliveryCount += Math.max(0, group.length - 1);
  }
  representatives.sort(compareCommands);
  for (const command of representatives) {
    refreshMovement(work, variant, command.atMs);
    applyCommand(work, command, variant);
    refreshMovement(work, variant, command.atMs);
  }

  if (work.manifest) {
    for (const currentRole of HEART_REGENT_ROLES) {
      const current = work.functions[currentRole];
      current.coverage = Math.min(1, current.acceptedUnits / work.manifest.roleDemand[currentRole]);
    }
    refreshMovement(work, variant, lastServerNow);
  }

  const functions = functionSnapshots(work, variant);
  const anchors = anchorSnapshot(work, variant);
  const usefulByRole = { ...work.usefulContributionByRole };
  const usefulByAffiliation = { ...work.usefulContributionByAffiliation };
  const metrics: HeartRegentMetrics = {
    logicalMutationCount: work.logicalMutationCount,
    deliveryCount: deliveries.length,
    duplicateDeliveryCount: work.duplicateDeliveryCount,
    divergentDuplicateCount: work.divergentDuplicateCount,
    rejectedCommandCount: work.rejections.length,
    wrongChronicleCount: work.wrongChronicleCount,
    wrongManifestCount: work.wrongManifestCount,
    wrongAttemptCount: work.wrongAttemptCount,
    oldReceiptOrAttemptCount: work.oldReceiptOrAttemptCount,
    disconnectCount: work.disconnectCount,
    crashCount: work.crashCount,
    supplyInterruptCount: work.supplyInterruptCount,
    combatCoreCalls: work.combatCoreCalls,
    combatResolvedBeforeWindow: work.combatResolvedBeforeWindow,
    combatResolvedInsideWindow: work.combatResolvedInsideWindow,
    combatProgress: round6(work.combatProgress),
    combatRequirement: round6(work.combatRequirement),
    capBindingCount: work.capBindingCount,
    usefulContributionByRole: usefulByRole,
    usefulContributionByAffiliation: usefulByAffiliation,
    movementTrace: [...work.movementTrace],
    outcomeHistory: work.outcomeHistory.map((receipt) => receipt.type),
    currentReceiptCount: work.receipt ? 1 : 0,
    currentDossierCount: work.repelledDossier ? 1 : 0,
    terminalDuplicateNoLogicalMutation: work.terminalDuplicateNoLogicalMutation &&
      work.duplicateDeliveryCount >= 0,
    supplyChainIntact: work.supplyChainIntact,
    windowOpened: work.windowOpenedAtMs !== null,
    contentionWindowEndsAtMs: work.contentionWindowEndsAtMs
  };
  return freezeDeep({
    schema: HEART_REGENT_SCHEMA,
    variant,
    lastServerNow,
    deliveries: clone(deliveries),
    phase: work.phase,
    movement: work.movement,
    attempt: work.attempt,
    manifest: work.manifest,
    functions,
    anchors,
    pressure: Math.max(0, Math.round(work.pressure)),
    windowOpenedAtMs: work.windowOpenedAtMs,
    contentionWindowEndsAtMs: work.contentionWindowEndsAtMs,
    combatProgress: round6(work.combatProgress),
    combatRequirement: round6(work.combatRequirement),
    movementTrace: [...work.movementTrace],
    receipt: work.receipt,
    repelledDossier: work.repelledDossier,
    outcomeHistory: work.outcomeHistory,
    settlementCause: work.settlementCause,
    rejections: [...work.rejections].sort((left, right) => left.commandId.localeCompare(right.commandId) || left.code.localeCompare(right.code)),
    metrics
  });
}

export function createHeartRegentState(variantValue: HeartRegentVariant): HeartRegentState {
  const variant = normalizeVariant(variantValue);
  return deriveState(variant, [], 0);
}

export function reduceHeartRegent(
  stateValue: HeartRegentState,
  commandValue: HeartRegentCommand,
  serverNowValue: number
): HeartRegentState {
  if (!safeInteger(serverNowValue) || serverNowValue < 0) throw new Error("heart_regent_server_clock_invalid");
  if (stateValue.schema !== HEART_REGENT_SCHEMA) throw new Error("heart_regent_state_schema_invalid");
  const command = normalizeCommand(commandValue);
  const deliveryId = command.deliveryId!;
  if (stateValue.deliveries.some((delivery) => delivery.deliveryId === deliveryId)) return stateValue;
  const deliveries = [...stateValue.deliveries, command].sort(compareCommands);
  return deriveState(stateValue.variant, deliveries, Math.max(stateValue.lastServerNow, serverNowValue));
}

export const applyHeartRegentCommand = reduceHeartRegent;
export const reduceHeartRegentState = reduceHeartRegent;

export function projectHeartRegentPublic(state: HeartRegentState): HeartRegentPublicProjection {
  const functions = Object.fromEntries(HEART_REGENT_ROLES.map((currentRole) => {
    const current = state.functions[currentRole];
    const functionState = !current.thresholdMet ? (current.coverage > 0 ? "partial" : "missing") : "met";
    return [currentRole, {
      state: functionState,
      coverage: current.coverage,
      thresholdMet: current.thresholdMet,
      usefulContributionObserved: current.usefulContributionObserved
    }];
  })) as HeartRegentPublicSummary["functions"];
  const anchorState = state.anchors.requiredCount === 0 || !state.anchors.ready
    ? (state.anchors.heldCount === 0 ? "missing" : "strained")
    : (state.anchors.brokenCount > 0 ? "strained" : "stable");
  const signal = state.pressure >= 260 ? "critical" : state.pressure >= 160 ? "rising" : "stable";
  const summary: HeartRegentPublicSummary = {
    movement: state.movement,
    functions,
    pressure: { aggregate: state.pressure, signal },
    anchors: {
      activeCount: state.anchors.activeCount,
      requiredCount: state.anchors.requiredCount,
      brokenCount: state.anchors.brokenCount,
      toleratedBreaks: state.anchors.toleratedBreaks,
      state: anchorState
    },
    blockingCause: state.phase === "contained" ? "contained" :
      state.settlementCause ?? blockingCauseFromState(state),
    nextActions: state.phase === "contained" || state.phase === "repelled"
      ? []
      : nextActionsFromState(state),
    globalDecision: "requires_all_five_functions_and_contention"
  };
  return freezeDeep({
    schema: HEART_REGENT_PUBLIC_SCHEMA,
    phase: state.phase,
    verdict: state.phase === "contained" || state.phase === "repelled" ? state.phase : "pending",
    attempt: state.attempt,
    summary,
    receipts: {
      contained: state.receipt?.type === "contained",
      repelled: state.receipt?.type === "repelled"
    }
  });
}

function blockingCauseFromState(state: HeartRegentState): string {
  if (!state.manifest) return "manifest_missing";
  const missing = HEART_REGENT_ROLES.filter((currentRole) => !state.functions[currentRole].thresholdMet);
  if (missing.length > 0) return `missing_function:${missing.join(",")}`;
  if (state.windowOpenedAtMs === null) return "vulnerability_window_missing";
  if (!state.anchors.ready) return "anchors_unstable";
  if (!state.metrics.supplyChainIntact) return "supply_chain_interrupted";
  if (state.combatProgress < state.combatRequirement) return "combat_progress_insufficient";
  if (state.movement !== "contention") return "contention_not_open";
  if (state.contentionWindowEndsAtMs !== null && state.lastServerNow > state.contentionWindowEndsAtMs) {
    return "contention_window_expired";
  }
  return "settlement_pending";
}

function nextActionsFromState(state: HeartRegentState): readonly string[] {
  if (!state.manifest) return ["lock-manifest"];
  const actions: string[] = [];
  for (const currentRole of HEART_REGENT_ROLES) {
    if (!state.functions[currentRole].thresholdMet) actions.push(`contribute:${currentRole}`);
  }
  if (state.windowOpenedAtMs === null) {
    if (!state.functions.observer.usefulContributionObserved) actions.push("contribute:observer");
    if (!state.functions.purifier.usefulContributionObserved) actions.push("contribute:purifier");
  }
  if (!state.metrics.supplyChainIntact) actions.push("restore:supply-chain");
  if (!state.anchors.ready) actions.push("repair:anchors");
  if (state.combatProgress < state.combatRequirement) actions.push("contribute:combattre");
  if (state.movement === "contention") actions.push("settle");
  return [...new Set(actions)].slice(0, 3);
}

export function serializeHeartRegentState(state: HeartRegentState): string {
  return stable(state);
}

export function restoreHeartRegentState(serialized: string): HeartRegentState {
  let parsed: unknown;
  try {
    parsed = JSON.parse(serialized);
  } catch {
    throw new Error("heart_regent_state_serialized_invalid");
  }
  if (!isRecord(parsed) || parsed.schema !== HEART_REGENT_SCHEMA || !isRecord(parsed.variant) ||
    !Array.isArray(parsed.deliveries) || !safeInteger(parsed.lastServerNow)) {
    throw new Error("heart_regent_state_shape_invalid");
  }
  const variant = normalizeVariant(parsed.variant);
  const deliveries = parsed.deliveries.map((delivery) => normalizeCommand(delivery as HeartRegentCommand));
  return deriveState(variant, deliveries, parsed.lastServerNow);
}

export function getHeartRegentBenchMetrics(state: HeartRegentState): HeartRegentBenchMetrics {
  return {
    ...state.metrics,
    functions: state.functions,
    anchors: state.anchors,
    pressure: state.pressure,
    blockingCause: blockingCauseFromState(state),
    nextActions: nextActionsFromState(state)
  };
}
